import { useEffect, useRef } from "react"

/**
 * The session-gating logic behind the dashboard's auth gate.
 *
 * Presentation deliberately stays in the app. What lives here is the part that
 * would be dangerous to have two copies of once Contract Maker becomes its own
 * deployable app: deciding when a session is about to lapse, telling a lapsed
 * session apart from a visitor who was never signed in, and scrubbing the
 * verification parameters out of the address bar. A second app rendering its
 * own dialogs is harmless; a second app disagreeing about when a session has
 * expired is not.
 *
 * Everything here is React-and-nothing-else on purpose, so sharing the gate
 * never drags a UI library into the auth package.
 */

export type DashboardSessionExpiry = {
  absoluteExpiresAt?: number
  expiresAt: number
  idleExpiresAt?: number
}

export type DashboardEmailVerificationNotice = {
  message: string
}

/** How long before expiry the user is warned. */
export const DASHBOARD_SESSION_EXPIRY_WARNING_MS = 5 * 60 * 1000

/**
 * The soonest of the session's expiry stamps.
 *
 * A session ends at whichever limit is reached first — the absolute cap, the
 * idle timeout or the token's own expiry — so the warning has to track the
 * minimum rather than any single one of them.
 */
export function earliestSessionExpiry(session: DashboardSessionExpiry): number | null {
  const candidates = [session.expiresAt, session.idleExpiresAt, session.absoluteExpiresAt].filter(
    (value): value is number => typeof value === "number" && Number.isFinite(value)
  )
  if (candidates.length === 0) return null
  return Math.min(...candidates)
}

/**
 * Reads Auth0's email-verification result out of a query string.
 *
 * Auth0 signals success several different ways depending on the connection and
 * the flow, which is why this accepts more than one shape rather than pinning
 * one parameter.
 */
export function readEmailVerificationNotice(search: string): DashboardEmailVerificationNotice | null {
  const searchParams = new URLSearchParams(search)
  const success = searchParams.get("success")?.toLowerCase()
  const code = searchParams.get("code")?.toLowerCase()
  const message = searchParams.get("message")?.trim()
  const emailVerified = searchParams.get("email_verified")?.toLowerCase()
  const verification = searchParams.get("verification")?.toLowerCase()
  const looksVerified = message ? /verified/i.test(message) : false

  if (
    emailVerified === "true" ||
    verification === "success" ||
    (success === "true" && (code === "success" || looksVerified))
  ) {
    return { message: message || "" }
  }

  return null
}

/** Parameters stripped from the URL once the notice has been shown. */
const EMAIL_VERIFICATION_PARAMS = [
  "code",
  "email",
  "email_verified",
  "message",
  "success",
  "supportForgotPassword",
  "supportSignUp",
  "verification",
]

/**
 * The same URL with the verification parameters removed.
 *
 * Returned rather than applied so the caller owns the history entry, and so
 * the scrubbing itself can be tested without a document. The address bar keeps
 * the user's email otherwise, which then rides along into any link they share.
 */
export function urlWithoutEmailVerificationParams(href: string): string {
  const url = new URL(href)
  for (const key of EMAIL_VERIFICATION_PARAMS) url.searchParams.delete(key)
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Tracks whether this visitor was ever signed in during the current mount.
 *
 * Distinguishes a session that ran out from someone who simply arrived signed
 * out. Only the former should be told their session expired; the latter is
 * sent to the login page without an alarming dialog.
 */
export function useHasBeenAuthenticated(isAuthenticated: boolean): boolean {
  const hasBeenAuthenticatedRef = useRef(false)
  if (isAuthenticated) hasBeenAuthenticatedRef.current = true
  return hasBeenAuthenticatedRef.current
}

/**
 * Calls `onChange` when the expiry warning should be shown or hidden.
 *
 * Fires immediately when the session is already inside the warning window,
 * because a timer scheduled with a negative delay would otherwise fire at once
 * anyway and a session restored from storage can arrive already close to
 * lapsing.
 */
export function useSessionExpiryWarning({
  enabled,
  onChange,
  session,
  warningMs = DASHBOARD_SESSION_EXPIRY_WARNING_MS,
}: {
  enabled: boolean
  onChange: (shouldWarn: boolean) => void
  session: DashboardSessionExpiry | null | undefined
  warningMs?: number
}) {
  // Held in a ref so an inline callback does not reschedule the timer.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (typeof window === "undefined") return
    if (!enabled || !session) {
      onChangeRef.current(false)
      return
    }

    const expiresAt = earliestSessionExpiry(session)
    // Falsy rather than nullish, matching the behaviour this was extracted
    // from: an expiry stamp of 0 is treated as "no usable expiry" and warns
    // about nothing, rather than firing the warning immediately.
    if (!expiresAt) return

    const warningDelay = expiresAt - Date.now() - warningMs
    if (warningDelay <= 0) {
      onChangeRef.current(true)
      return
    }

    onChangeRef.current(false)
    const timeout = window.setTimeout(() => onChangeRef.current(true), warningDelay)
    return () => window.clearTimeout(timeout)
  }, [enabled, session, warningMs])
}
